import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Check, Copy, Lightbulb, Mail, RefreshCw, Wand2 } from "lucide-react";

type FieldDef = {
  key: string;
  label: string;
  placeholder?: string;
  multiline?: boolean;
  options?: { value: string; label: string }[];
};

type EmailTypeDef = {
  id: string;
  label: string;
  description: string;
  fields: FieldDef[];
  checklist: string[];
};

const HIGHLIGHT_FIELD: FieldDef = { key: "highlightLink", label: "Highlight Video Link", placeholder: "https://youtube.com/..." };
const NEXT_EVENT_FIELD: FieldDef = { key: "nextEvent", label: "Where Can They See You Play Next?", placeholder: "e.g. Fall Classic, Nov 7-9, Scottsdale" };

// Ids and field keys must match COACH_EMAIL_TYPES and DETAIL_LABELS in server/coachEmail.ts
const EMAIL_TYPES: EmailTypeDef[] = [
  {
    id: "intro",
    label: "First Introduction",
    description: "Your first email to a coach who doesn't know you yet.",
    fields: [
      { key: "whyThisSchool", label: "Why This School?", placeholder: "e.g. Their nursing program, how they play, a player you look up to", multiline: true },
      HIGHLIGHT_FIELD,
      NEXT_EVENT_FIELD,
    ],
    checklist: [
      "Send it from the athlete's own email, not a parent's",
      "Spell the coach's name and school correctly",
      "Include a highlight video link",
      "Give one real reason you want this school",
    ],
  },
  {
    id: "followup",
    label: "Follow-Up",
    description: "You've emailed or met this coach and have something new to share.",
    fields: [
      { key: "lastContact", label: "When Did You Last Connect?", placeholder: "e.g. Emailed in March, no reply yet / Talked at their camp in June" },
      { key: "whatsNew", label: "What's New Since Then?", placeholder: "e.g. Named All-Region, new highlight video, hitting .400", multiline: true },
      HIGHLIGHT_FIELD,
    ],
    checklist: [
      "Lead with one new thing, not \"just checking in\"",
      "Mention when and how you last connected",
      "Ask one specific, easy question",
      "Never guilt-trip a coach for not replying",
    ],
  },
  {
    id: "visit",
    label: "Request Campus Visit",
    description: "Ask to visit campus and meet the coaching staff.",
    fields: [
      {
        key: "visitType",
        label: "Visit Type",
        options: [
          { value: "unofficial", label: "Unofficial (family pays)" },
          { value: "official", label: "Official (the coach invited me)" },
        ],
      },
      { key: "visitDates", label: "Dates You Can Visit", placeholder: "e.g. Oct 10-12, or any weekend in November" },
      { key: "whoIsComing", label: "Who's Coming?", placeholder: "e.g. Me and my parents" },
      { key: "visitGoals", label: "What Do You Want to See or Do?", placeholder: "e.g. Meet the staff, watch a practice, tour the nursing school", multiline: true },
    ],
    checklist: [
      "Offer specific dates, and say you're flexible",
      "Check the team's schedule and avoid game days",
      "Unofficial visits are paid by your family; official visits come from a coach's invite",
    ],
  },
  {
    id: "camp",
    label: "Camp / Showcase Notice",
    description: "Tell a coach where and when to come watch you play.",
    fields: [
      { key: "eventName", label: "Event Name", placeholder: "e.g. Triple Crown Fall Classic" },
      { key: "eventDates", label: "Event Dates", placeholder: "e.g. Nov 7-9" },
      { key: "eventLocation", label: "Location", placeholder: "e.g. Scottsdale, AZ" },
      { key: "teamJersey", label: "Team and Jersey Number", placeholder: "e.g. Desert Storm 16U, #7" },
      { key: "schedule", label: "Your Schedule", placeholder: "One game per line, e.g.\nFri 11/7, 9:00 AM, Field 3 vs. AZ Heat", multiline: true },
    ],
    checklist: [
      "Send it 1 to 2 weeks before the event",
      "Put your jersey number in the subject line",
      "List every game time and field",
      "Send a thank-you if the coach comes to watch",
    ],
  },
  {
    id: "thankyou",
    label: "Thank You",
    description: "After a camp, call, visit, or offer.",
    fields: [
      {
        key: "thankingFor",
        label: "Thanking Them For",
        options: [
          { value: "your camp", label: "Camp" },
          { value: "the phone call", label: "Phone call" },
          { value: "the campus visit", label: "Campus visit" },
          { value: "the offer", label: "Offer" },
          { value: "taking the time to talk with me", label: "Conversation or meeting" },
        ],
      },
      { key: "thanksDetail", label: "One Specific Detail", placeholder: "e.g. The backhand footwork drill really clicked for me", multiline: true },
    ],
    checklist: [
      "Send it within 24 to 48 hours",
      "Mention one specific detail",
      "Keep it short, under 100 words",
      "Say what you'll work on next",
    ],
  },
  {
    id: "update",
    label: "Season / Highlight Update",
    description: "Share new stats, honors, or video with coaches on your list.",
    fields: [
      { key: "whatsNew", label: "What's New?", placeholder: "e.g. Hit .412 with 22 steals, named All-Region 1st Team", multiline: true },
      HIGHLIGHT_FIELD,
      NEXT_EVENT_FIELD,
    ],
    checklist: [
      "Lead with your biggest new result",
      "Include the season or event with every stat",
      "Add your newest highlight video",
      "Send updates every few months, not every week",
    ],
  },
];

const NOTES_FIELD: FieldDef = {
  key: "notes",
  label: "Anything Else? (optional)",
  placeholder: "Intended major, measurables, club coach contact...",
  multiline: true,
};

type EmailResult = { subject: string; body: string; tips?: string[]; source?: "ai" | "template" };

const labelClass = "text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1.5 block";
const inputClass = "bg-[#0B1220] border-[#415A77]/60 text-white placeholder:text-slate-500";

export default function CoachEmailWriter({ open, onClose, defaultCollege, athleteId, athleteName }: {
  open: boolean;
  onClose: () => void;
  defaultCollege?: string;
  athleteId?: string;
  athleteName?: string;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState({ collegeName: defaultCollege || "", coachName: "", emailType: "intro" });
  // Field values for every type live in one object, so switching types doesn't
  // wipe shared fields like the highlight link. Only the current type's keys are sent.
  const [details, setDetails] = useState<Record<string, string>>({});
  const [result, setResult] = useState<EmailResult | null>(null);
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setForm((f) => ({ ...f, collegeName: defaultCollege || "" })); setResult(null); }
  }, [open, defaultCollege]);

  const typeDef = EMAIL_TYPES.find((t) => t.id === form.emailType) ?? EMAIL_TYPES[0];
  const fields = [...typeDef.fields, NOTES_FIELD];

  const placeholderCount = useMemo(
    () => (`${draft.subject}\n${draft.body}`.match(/\[[^\]\n]+\]/g) || []).length,
    [draft]
  );

  const generate = async () => {
    if (!form.collegeName.trim()) return;
    setLoading(true);
    try {
      const sentDetails = Object.fromEntries(
        fields.map((f) => [f.key, details[f.key]?.trim() || ""]).filter(([, v]) => v)
      );
      const res = await apiRequest("/coach-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, athleteId, details: sentDetails }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || "Request failed");
      setResult(data);
      setDraft({ subject: data.subject, body: data.body });
    } catch (err) {
      toast({ title: "Email generation failed", description: err instanceof Error ? err.message : "Could not generate email. Try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text: string) => { navigator.clipboard.writeText(text); toast({ title: "Copied to clipboard" }); };
  const openInEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
  };

  const renderField = (f: FieldDef) => {
    const value = details[f.key] || "";
    const set = (v: string) => setDetails((d) => ({ ...d, [f.key]: v }));
    return (
      <div key={f.key}>
        <label className={labelClass}>{f.label}</label>
        {f.options ? (
          <Select value={value} onValueChange={set}>
            <SelectTrigger className="bg-[#0B1220] border-[#415A77]/60 text-white">
              <SelectValue placeholder="Choose one" />
            </SelectTrigger>
            <SelectContent className="bg-[#0D1B2A] border-[#415A77]/60 text-white">
              {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : f.multiline ? (
          <Textarea placeholder={f.placeholder} value={value} onChange={(e) => set(e.target.value)} className={`${inputClass} resize-none`} rows={f.key === "schedule" ? 4 : 2} />
        ) : (
          <Input placeholder={f.placeholder} value={value} onChange={(e) => set(e.target.value)} className={inputClass} />
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0D1B2A] border border-[#E0A458]/30 text-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-montserrat font-bold text-xl flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-[#E0A458]" /> Coach Email Writer
          </DialogTitle>
          {athleteName && <p className="text-sm text-slate-400">Writing as {athleteName}</p>}
        </DialogHeader>
        {!result ? (
          <div className="space-y-4 pt-2">
            <div>
              <label className={labelClass}>What Kind of Email?</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {EMAIL_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, emailType: t.id }))}
                    className={`rounded-lg border px-3 py-2 text-left text-sm font-semibold transition-colors ${
                      t.id === form.emailType
                        ? "border-[#E0A458] bg-[#E0A458]/10 text-white"
                        : "border-[#415A77]/60 bg-[#0B1220] text-slate-300 hover:border-[#E0A458]/50 hover:text-white"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-400 mt-2">{typeDef.description}</p>
            </div>
            <div className="rounded-lg border border-[#E0A458]/25 bg-[#1B263B]/60 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#E0A458] mb-2">What makes this email work</p>
              <ul className="space-y-1.5">
                {typeDef.checklist.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="h-3.5 w-3.5 text-[#E0A458] mt-0.5 shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>School *</label>
                <Input placeholder="e.g. University of Tennessee" value={form.collegeName} onChange={(e) => setForm((f) => ({ ...f, collegeName: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Coach Last Name</label>
                <Input placeholder="e.g. Smith" value={form.coachName} onChange={(e) => setForm((f) => ({ ...f, coachName: e.target.value }))} className={inputClass} />
              </div>
            </div>
            {fields.map(renderField)}
            <p className="text-xs text-slate-500">Stats, achievements, GPA, and your notes on this school are pulled from the athlete's profile automatically.</p>
            <Button onClick={generate} disabled={loading || !form.collegeName.trim()} className="w-full bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold">
              {loading ? "Writing your email..." : "Generate Email"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {result.source === "template" && (
              <div className="rounded-lg border border-[#415A77]/60 bg-[#1B263B]/60 p-3 text-sm text-slate-300">
                <span className="font-semibold text-white">Starter template.</span> The AI writer is unavailable right now, so here's a proven starting point for this email type. Personalize it before sending.
              </div>
            )}
            <div className="rounded-lg border border-[#415A77]/40 bg-[#0B1220] p-4 space-y-3">
              <div>
                <label className={labelClass}>Subject</label>
                <Input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} className="bg-[#0D1B2A] border-[#415A77]/60 text-white font-medium" />
              </div>
              <div>
                <label className={labelClass}>Email Body</label>
                <Textarea value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} className="bg-[#0D1B2A] border-[#415A77]/60 text-slate-200 text-sm leading-relaxed" rows={14} />
              </div>
            </div>
            {placeholderCount > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-[#E0A458]/40 bg-[#E0A458]/10 p-3 text-sm text-[#FDF1DE]">
                <AlertCircle className="h-4 w-4 text-[#E0A458] mt-0.5 shrink-0" />
                Fill in {placeholderCount} [bracketed] {placeholderCount === 1 ? "spot" : "spots"} with real info before sending.
              </div>
            )}
            {result.tips && result.tips.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
                  <Lightbulb className="h-3.5 w-3.5 text-[#E0A458]" /> Before you send
                </p>
                <ul className="space-y-1.5">
                  {result.tips.map((tip) => (
                    <li key={tip} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="h-3.5 w-3.5 text-[#E0A458] mt-0.5 shrink-0" /> {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={() => copyText(`Subject: ${draft.subject}\n\n${draft.body}`)} className="flex-1 bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold">
                <Copy className="h-4 w-4 mr-1.5" /> Copy Full Email
              </Button>
              <Button variant="outline" onClick={openInEmail} className="border-[#415A77]/60 text-slate-300 hover:text-white">
                <Mail className="h-4 w-4 mr-1.5" /> Open in Email
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={generate} disabled={loading} className="flex-1 border-[#415A77]/60 text-slate-300 hover:text-white">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "Rewriting..." : "Regenerate"}
              </Button>
              <Button variant="outline" onClick={() => setResult(null)} className="flex-1 border-[#415A77]/60 text-slate-300 hover:text-white">
                Change Details
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
